<template lang="pug">
  #todays-progress
    WtTitle.pb-4.title-border.is-flex.is-justify-content-space-between.is-align-items-center.is-flex-wrap-wrap(card-title) Today's Progress
      .progress-switch-align-container.pt-2(v-if="isPointsFeatureOn && !isProfile")
        WtButtonToggle(
          :activeToggle="activeToggle[progressViewMode]"
          @toggleOn="setProgressViewMode"
          leftButtonText="Steps"
          rightButtonText="Points"
        )
    .main-content(v-if="progressViewMode==='steps'")
        WtTile
          VmProgress(
            type="circle"
            :percentage="stepGoalPercentageProgress"
            strokeColor="#5fc0ab"
            :stroke-width="9"
            :width="120"
          )
            WtSvgIcon( v-if="goalAchieved" icon="icon_checkmark" width="120" height="80")
            WtSvgIcon(v-else icon="blue-shoe" width="120" height="80")
            //- [auth.user optimism]
        WtTile(h2)
          template(v-if="_.has(todaysMember, 'program') || programLoaded")
            //- [auth.user optimism]
            .todays-progress-footer.has-text-centered.has-text-grey.is-size-5(v-if="coreProgramMeta.enforceStepGoal")
              | Program Goal: {{ formatWithCommas(coreProgramMeta.defaultStepGoal) }} steps
            .todays-progress-footer.has-text-centered.has-text-grey.is-size-5(v-else)
              | Goal: {{ _.get(todaysMember, "memberData.stepGoal", " _ _ _ _ ").toLocaleString() }} steps
              .set-goal-button.is-size-7(
                v-if="!isProfile"
                @click="openModal({ stepGoal: sanitizeNum(_.get(todaysMember, 'memberData.stepGoal', 0)) })"
              )
                | Update your step goal
        WtTitle
          WtTitle(h1).has-text-centered {{ stepsAndActivities.toLocaleString() }}&nbsp;
          .subtitle.has-text-centered.is-size-5.has-text-grey Steps
            //- [auth.user optimism]
            .is-size-7.has-text-centered.has-text-grey(
              v-if="_.get(todaysMember, 'program.converter', false) || coreProgramMeta.converter"
            ) (includes converted activities)
    .main-content(v-else)
      .is-flex.is-justify-content-space-evenly.is-align-items-center.is-flex-grow-1.is-flex-wrap-wrap
        WtSvgIcon(icon="icon_star" width="80" height="80" )
        .points-container
          .all-time-points.is-size-1 {{ allTimePoints }}
          .all-time-points-label.is-flex.is-size-4.is-lh-4.is-align-items-flex-end.is-grey All-time Points
            span.ml-3.is-size-6
              WtRouterLink(
                to="Points"
              ) Details
      .is-flex.pt-4.mt-5.footer-border

</template>

<script>
import { mapGetters } from "vuex";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTile from "@@/WtTile/WtTile";
import { numberUtilityMixin } from "@/mixins/numberUtilityMixin";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import WtButton from "@@/WtButton/WtButton";
import WtRouterLink from "@@/WtRouterLink/WtRouterLink";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import WtButtonToggle from "@@/WtButtonToggle/WtButtonToggle";

const TodaysProgressModal = () =>
  import(
    /* webpackChunkName: "TodaysProgressModal" */ "@@/Members/TodaysProgress/TodaysProgressModal"
  ).catch(() => {
    window.location.reload();
  });

export default {
  name: "TodaysProgress",

  components: {
    WtSvgIcon,
    WtTitle,
    WtTile,
    TodaysProgressModal,
    WtButton,
    WtRouterLink,
    WtButtonToggle
  },

  mixins: [numberUtilityMixin, dateUtilityMixin],

  props: {
    isProfile: {
      type: Boolean,
      default: false
    },

    memberObject: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      stepData: null,
      stepsLastWeek: 0,
      stepsThisWeek: 0,
      stepsAverage: 0,
      progressViewMode: "steps",
      activeToggle: {
        steps: "left",
        points: "right"
      }
    };
  },

  computed: {
    ...mapGetters("program", ["coreProgramMeta", "programLoaded"]),
    ...mapGetters("program", ["isPointsFeatureOn"]),

    todaysMember() {
      return this.isProfile ? this.memberObject : this.$auth.user();
    },

    stepGoalPercentageProgress() {
      if (this.goalAchieved) {
        // the progress bar shows empty bar for greaterthan 100%, so
        return 100;
      } else {
        return this.stepGoalPercentage;
      }
    },

    stepGoalPercentage() {
      return Math.round((this.stepsAndActivities / this.stepGoal) * 100);
    },

    goalAchieved() {
      return this.stepGoalPercentage >= 100;
    },

    stepGoal() {
      return this._.get(this.coreProgramMeta, "enforceStepGoal", false)
        ? this.sanitizeNum(
            this._.get(this.coreProgramMeta, "defaultStepGoal", 8000)
          )
        : this.sanitizeNum(
            this._.get(this.todaysMember, "memberData.stepGoal", 8000)
          );
    },

    stepsAndActivities() {
      return this.sanitizeNum(
        this._.get(this.todaysMember, "walkData.todaysStepsAndActivities", 0)
      );
    },

    allTimePoints() {
      return this.todaysMember.memberData.totalPoints;
    }
  },

  watch: {
    stepData() {
      // fetchAllPageMyDashboard, dashboard chart, entry modal, sets members.stepsData
      // so we recalculate after stepsData updates
      this.calculateStats();
    },

    todaysMember: {
      handler: function() {
        if (this._.has(this.todaysMember, "id")) {
          this.fetchData();
        }
      },
      immediate: true
    }
  },

  methods: {
    openModal(props) {
      this.$buefy.modal.open({
        parent: this,
        props: props,
        component: TodaysProgressModal,
        hasModalCard: true
      });
    },

    async fetchData() {
      // get data for at least the last two weeks
      let days = 14;

      try {
        // don't use the fetchMemberStepData action since that sets the stepsData getter ...
        let response = await this.axios.get(
          `members/${this.todaysMember.id}/step-data`,
          { params: { perPage: days } }
        );
        this.stepData = response.data.data;
      } catch (error) {
        this.stepData = null;
      }
    },

    // getAllTimePoints() {
    //   return this.todaysMember.memberData.totalPoints;
    // },

    getStepsAndActivitiesSum(array) {
      return array.reduce(
        (sum, current) => sum + current.stepsAndActivities,
        0
      );
    },

    calculateStats() {
      const sundayOfThisWeekObject = this.moment().startOf("week");
      const sundayOfThisWeekString = this.date(
        sundayOfThisWeekObject,
        "DATE_FORMAT_DB"
      ).formatted;

      const sundayOfLastWeekString = this.date(
        sundayOfThisWeekObject.subtract(1, "week"),
        "DATE_FORMAT_DB"
      ).formatted;

      const sevenDaysAgoString = this.date(
        this.moment().subtract(6, "days"), // including today gives the 7 day average, right?!
        "DATE_FORMAT_DB"
      ).formatted;

      // sort the data
      let lastWeekData = [];
      let thisWeekData = [];
      let last7daysData = [];
      for (let i = 0; i < this.stepData.length; i++) {
        let dataCopy = this.stepData[i];
        let isLastWeeksData =
          this.moment(dataCopy.created).isSameOrAfter(sundayOfLastWeekString) &&
          this.moment(dataCopy.created).isBefore(sundayOfThisWeekString);
        let isThisWeeksData = this.moment(dataCopy.created).isSameOrAfter(
          sundayOfThisWeekString
        );
        if (isLastWeeksData) {
          lastWeekData.push(dataCopy);
        }
        if (isThisWeeksData) {
          thisWeekData.push(dataCopy);
        }
        if (this.moment(dataCopy.created).isSameOrAfter(sevenDaysAgoString)) {
          last7daysData.push(dataCopy);
        }
      }
      // calculate stats
      this.stepsLastWeek = this.getStepsAndActivitiesSum(
        lastWeekData
      ).toLocaleString();
      this.stepsThisWeek = this.getStepsAndActivitiesSum(
        thisWeekData
      ).toLocaleString();
      this.stepsAverage = Math.floor(
        this.getStepsAndActivitiesSum(last7daysData) / 7
      ).toLocaleString();
    },

    setProgressViewMode(activeToggle) {
      this.progressViewMode = activeToggle === "left" ? "steps" : "points";
    }
  }
};
</script>

<style lang="sass">
#todays-progress
  height: 100%
  display: flex
  flex-direction: column
  //justify-content: space-between
  .main-content
    flex-grow: 1
    //background: red
    display: flex
    flex-direction: column
    justify-content: space-between

  .progress-circle
    z-index: 0

  .set-goal-button
    color: $dodger-blue

    &:hover
      cursor: pointer

  .title-border
    border-bottom: .5px solid $grey-light

  .footer-border
    border-top: .5px solid $grey-light

  .custom-responsive-column
    @media screen and (max-width: 1215px)
      display: block

  .stat-container
    min-width: 90px

  .progress-switch-align-container
    max-width: 100%
    display: flex
    justify-content: center
    flex-grow: 1

  .all-time-points-label
    white-space: nowrap
    line-height: $size-4

  .is-grey
    color: $grey;

  .all-time-points
    @media screen and (max-width: 1215px) and (min-width: 769px)
      text-align: center
  .tile
    display: flex
    justify-content: center
    align-items: center
</style>
